import { useState } from "react";
import AccordionOverview from "../../../../components/AccordionOverview/AccordionOverview";
import { ApplicantInfoOverview } from "../../../../components/Applicant/ApplicantInfoOverview";
import { OverviewHeader } from "../../../../components/Header/OverviewHeader";
import type { CommonProcess } from "../../../../types/common";
import { updatePersonName } from "../../../../utils/contact.helper";
import {
  getFirstOffer,
  getUpdatedAppliedValues
} from "../../../../utils/offer.helper";
import { getLatestStatisticaVariables } from "../../../../utils/statistica.helper";
import { ApplicantRow } from "../../rows/se/applicantRow";
import { ApplicationtRow } from "../../rows/se/applicationRow";
import { ExistingLoanDetailsRow } from "../../rows/se/existingLoanDetailsRow";
import { FinancialRow } from "../../rows/se/financialRow";
import { OfferRow } from "../../rows/se/offerRow";
import { TaskRow } from "../../rows/se/TaskRow";
import { OverviewWrapper, ProcessWrapper, RowsWrapper } from "../../styles";
import { Modals, ModalTypes } from "../modals";

const LoanOverviewSe = (flow: CommonProcess) => {
  const { data, flowId } = flow?.caseData ?? {};
  const { country, agentApiApplication: agentApi } = data || {};
  const { applicant, coapplicant } = agentApi || {};
  const status = flow?.caseData?.status;
  const hasExistingLoans =
    (data?.integrations?.leabank?.ownExperience?.data?.mainApplicant
      ?.accountStatus?.activeLoansCount ?? 0) > 0;
  const [selectedModal, setSelectedModal] = useState<string>("");

  function handleModalClose() {
    setSelectedModal("");
  }

  // Create a deep copy to preserve original data before updatePersonName modifies it
  const latestData = data ? structuredClone(data) : data;
  getUpdatedAppliedValues(latestData);
  getLatestStatisticaVariables(latestData);

  const { applicant: latestApplicant, coapplicant: latestCoapplicant } =
    latestData?.agentApiApplication || {};
  if (
    latestData?.integrations?.bisnode ||
    latestData?.caseWork?.["manual-changed-values"]
  ) {
    if (latestApplicant) {
      updatePersonName(latestData, latestApplicant, true);
    }

    if (latestCoapplicant) {
      updatePersonName(latestData, latestCoapplicant, false);
    }
  }

  const initialData = data ? structuredClone(data) : data;
  getFirstOffer(initialData);

  return (
    <OverviewWrapper>
      <OverviewHeader country={country} label="Loan application" flow={flow} />
      <ProcessWrapper>
        <AccordionOverview title="Latest Information" startsExpanded>
          <ApplicantInfoOverview
            applicant={latestApplicant}
            coapplicant={latestCoapplicant}
          />
          {data?.businessStatus === "manual-check-final" && (
            <TaskRow data={data} />
          )}
          <RowsWrapper className="general information">
            <div className="left-side" style={{ marginRight: "10px" }}>
              <ApplicantRow data={data} />
            </div>
            <div className="right-side" style={{ marginLeft: "10px" }}>
              <ApplicationtRow
                data={latestData}
                flowId={flowId}
                handleModalClick={() => setSelectedModal(ModalTypes.KYC)}
              />
            </div>
          </RowsWrapper>
          <FinancialRow data={latestData} />
          <OfferRow data={latestData} status={status} />
          {hasExistingLoans && <ExistingLoanDetailsRow data={data} />}
          <Modals
            flow={flow}
            modalToShow={selectedModal}
            handleModalClose={handleModalClose}
          />
        </AccordionOverview>

        <AccordionOverview title="Initial Information" startsExpanded={false}>
          <ApplicantInfoOverview
            applicant={applicant}
            coapplicant={coapplicant}
          />
          <RowsWrapper className="general information">
            <div className="left-side" style={{ marginRight: "10px" }}>
              <ApplicantRow data={data} />
            </div>
            <div className="right-side" style={{ marginLeft: "10px" }}>
              <ApplicationtRow
                data={initialData}
                flowId={flowId}
                handleModalClick={() => setSelectedModal(ModalTypes.KYC)}
              />
            </div>
          </RowsWrapper>
          <FinancialRow data={initialData} />
          <OfferRow data={initialData} status={status} />
          {hasExistingLoans && <ExistingLoanDetailsRow data={data} />}
          <Modals
            flow={flow}
            modalToShow={selectedModal}
            handleModalClose={handleModalClose}
          />
        </AccordionOverview>
      </ProcessWrapper>
    </OverviewWrapper>
  );
};

export default LoanOverviewSe;
